import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Menu, X, Globe } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/contexts/AuthContext'
import { MwLogo } from './MwLogo'
import { MwButton } from './MwButton'
import { changeLanguage, type SupportedLanguage } from '@/i18n'
import { usePlatformSettings } from '@/hooks/usePlatformSettings'

function useNavLinks(isLoggedIn: boolean) {
  const { canBuyDirect, showsBusiness } = usePlatformSettings()
  const { t } = useTranslation('mw')

  // Con sesion iniciada la barra se quedaba vacia: solo el logo y la
  // pastilla del perfil. Desde dentro de un curso no habia manera de
  // volver a los cursos ni de llegar a las calculadoras sin pasar por
  // el logo, que no parece un enlace. Estos tres son los sitios a los
  // que un alumno vuelve todo el rato.
  if (isLoggedIn) {
    // Dos entradas, y ninguna repite lo que ya hace otra cosa.
    //
    // Aqui habia un tercer enlace, "Mi perfil", que llevaba a
    // /mi-ruta. La pastilla del avatar tambien pone "Mi perfil" y
    // lleva a /perfil. Dos sitios con el mismo nombre y distinto
    // destino: uno abria el resultado del diagnostico y el otro los
    // datos de la cuenta. /mi-ruta se llega desde el panel, donde
    // esta en su contexto.
    return [
      { label: t('nav.myCourses'), href: '/panel' },
      { label: t('nav.calculators'), href: '/calculadoras' },
    ]
  }

  const links = [
    { label: t('nav.diagnostic'), href: '/diagnostico' },
    { label: t('nav.calculators'), href: '/calculadoras' },
  ]

  if (canBuyDirect) {
    links.push({ label: t('nav.pricing'), href: '/comprar' })
  }
  if (showsBusiness) {
    links.push({ label: t('nav.companies'), href: '/empresas' })
  }

  return links
}

export function MwNavBar() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const { i18n, t } = useTranslation('mw')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const currentLang = (i18n.language ?? 'es') as SupportedLanguage
  const toggleLang = () => { setMobileMenuOpen(false); changeLanguage(currentLang === 'es' ? 'en' : 'es') }
  const isLoggedIn = Boolean(user)

  // El nombre completo si lo hay; si no, el prefijo del correo
  // recortado, porque el original desborda la pastilla.
  const rawName = user?.name?.trim() || user?.email?.split('@')[0] || ''
  const displayName = rawName.length > 14 ? `${rawName.slice(0, 13)}...` : rawName
  const navLinks = useNavLinks(isLoggedIn)

  const handleNav = (href: string) => {
    setMobileMenuOpen(false)
    navigate(href)
  }

  return (
    <nav
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 40,
        background: 'white',
        borderBottom: '1px solid var(--mw-border, #E5E7EB)',
        fontFamily: 'var(--mw-font-body)',
      }}
    >
      <div
        className="flex items-center justify-between"
        style={{ maxWidth: 'var(--mw-w-page)', margin: '0 auto', padding: '0 var(--mw-p-page)', height: 56 }}
      >
        {/* Logo */}
        <a
          href={isLoggedIn ? '/panel' : '/'}
          onClick={(e) => { e.preventDefault(); handleNav(isLoggedIn ? '/panel' : '/') }}
          className="no-underline shrink-0"
        >
          <MwLogo />
        </a>

        {/* Desktop: links + Globe + CTA */}
        <div className="hidden md:flex items-center gap-1">
          {navLinks.map(({ label, href }) => (
            <a
              key={href}
              href={href}
              onClick={(e) => { e.preventDefault(); handleNav(href) }}
              className="px-3 py-1.5 rounded-lg no-underline transition-colors"
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: location.pathname === href ? '#131830' : '#6B7280',
              }}
            >
              {label}
            </a>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-2">
          {!isLoggedIn && (
          <button
            onClick={toggleLang}
            aria-label={currentLang === 'es' ? 'Switch to English' : 'Cambiar a Español'}
            className="flex items-center gap-1 h-9 px-2.5 rounded-lg"
            style={{
              background: 'transparent',
              border: '1px solid var(--mw-border)',
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--mw-ink-secondary)',
              cursor: 'pointer',
            }}
          >
            <Globe className="size-3.5" aria-hidden />
            {currentLang.toUpperCase()}
          </button>
          )}

          {!isLoggedIn && (
            <MwButton variant="secondary" size="sm" onClick={() => handleNav('/login')}>
              {t('nav.login')}
            </MwButton>
          )}
          {isLoggedIn && (
            <button
              onClick={() => handleNav('/perfil')}
              className="flex items-center gap-2 h-9 px-3 rounded-full"
              style={{
                background: 'var(--mw-ink)',
                border: 'none',
                color: 'white',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <div className="size-6 rounded-full flex items-center justify-center"
                style={{ background: 'var(--mw-accent)', fontSize: 11, fontWeight: 700 }}>
                {(user?.name || user?.email || '?').charAt(0).toUpperCase()}
              </div>
              {displayName}
            </button>
          )}
        </div>

        {/* Mobile: hamburguesa siempre. Antes solo salia para los
            anonimos porque el alumno no tenia enlaces; ahora si los
            tiene y sin esto quedaban fuera del alcance en el movil. */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden flex items-center justify-center h-10 w-10 rounded-lg"
          style={{
            background: 'transparent',
            border: '1px solid var(--mw-border)',
            color: 'var(--mw-ink)',
            cursor: 'pointer',
          }}
          aria-label="Menú"
        >
          {mobileMenuOpen ? <X className="size-4" /> : <Menu className="size-4" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div style={{ borderTop: '1px solid #E5E7EB', background: 'white', padding: '12px 16px 20px' }}>
          <div className="flex flex-col gap-0.5">
            {navLinks.map(({ label, href }) => (
              <a
                key={href}
                href={href}
                onClick={(e) => { e.preventDefault(); handleNav(href) }}
                className="py-3 px-3 rounded-lg no-underline"
                style={{
                  fontSize: 15,
                  fontWeight: 500,
                  color: location.pathname === href ? '#2563EB' : '#131830',
                  background: location.pathname === href ? '#EFF4FF' : 'transparent',
                }}
              >
                {label}
              </a>
            ))}
          </div>

          <div className="flex flex-col gap-2 pt-3 mt-2" style={{ borderTop: '1px solid #E5E7EB' }}>
            {/* CTA */}
            {!isLoggedIn ? (
              <MwButton variant="primary" size="lg" onClick={() => handleNav('/login')}
                style={{ width: '100%', justifyContent: 'center' }}>
                {t('nav.login')}
              </MwButton>
            ) : (
              <button
                onClick={() => handleNav('/perfil')}
                className="flex items-center justify-center gap-2 h-11 w-full rounded-lg"
                style={{ background: 'var(--mw-ink)', border: 'none', color: 'white', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                <div className="size-7 rounded-full flex items-center justify-center"
                  style={{ background: 'var(--mw-accent)', fontSize: 12, fontWeight: 700 }}>
                  {(user?.name || user?.email || '?').charAt(0).toUpperCase()}
                </div>
                {displayName}
              </button>
            )}

            {/* Language toggle */}
            <button
              onClick={toggleLang}
              className="flex items-center justify-center gap-2 h-10 rounded-lg"
              style={{
                background: 'transparent',
                border: '1px solid var(--mw-border)',
                fontSize: 13,
                fontWeight: 500,
                color: 'var(--mw-ink-secondary)',
                cursor: 'pointer',
                width: '100%',
              }}
            >
              <Globe className="size-3.5" aria-hidden />
              {currentLang === 'es' ? 'English' : 'Español'}
            </button>
          </div>
        </div>
      )}
    </nav>
  )
}
